import { Controller } from "@hotwired/stimulus";
import { Marked } from "marked";
import renderResourceChip from "./resource_link_renderer";
import renderHexSwatches from "./color_swatch_renderer";
import linkAttributes from "./link_attributes";

const MESSAGE_TYPES = { email: "Email", sms: "SMS" };

const ICON_SVG = {
  email: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`,
  sms: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>`,
};

function detectMessageType(text) {
  const firstLine = text.trim().split("\n")[0].trim();
  for (const [key, label] of Object.entries(MESSAGE_TYPES)) {
    if (firstLine === label) return key;
  }
  return null;
}

function stripTypeLabel(html, label) {
  // Remove label from first <p> — either the whole <p> or just the leading text + <br>
  return html
    .replace(new RegExp(`^\\s*<p>\\s*${label}\\s*</p>`, "i"), "")
    .replace(new RegExp(`^\\s*<p>\\s*${label}\\s*<br>`, "i"), "<p>")
    .trim();
}

const md = new Marked({
  gfm: true,
  breaks: true,
  renderer: {
    blockquote(token) {
      const type = detectMessageType(token.text);
      if (type) {
        const body = this.parser.parse(token.tokens);
        const inner = stripTypeLabel(body, MESSAGE_TYPES[type]);
        return `<div class="my-3">
          <span class="flex items-center gap-1 text-sm font-bold text-gray-800 mb-1">${ICON_SVG[type]} ${MESSAGE_TYPES[type]}</span>
          <div class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 not-prose text-sm text-gray-800 [&>p]:my-2 [&>:first-child]:!mt-0 [&>:last-child]:!mb-0 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4">${inner}</div>
        </div>`;
      }
      return `<blockquote>${this.parser.parse(token.tokens)}</blockquote>`;
    },
    link({ href, title, text }) {
      if (title && title.startsWith("resource:")) {
        const type = title.slice("resource:".length);
        return renderResourceChip(text, href, type);
      }

      const titleAttr = title ? ` title="${title}"` : "";
      const url = new URL(href, window.location.origin);
      const isAdmin = url.origin === window.location.origin && url.pathname.startsWith("/admin");

      return `<a href="${href}"${titleAttr}${linkAttributes({ inPlace: isAdmin })}>${text}</a>`;
    },
  },
});

export default class extends Controller {
  static values = { content: String };

  contentValueChanged() {
    if (!this.contentValue) return;
    this.element.innerHTML = renderHexSwatches(md.parse(this.contentValue));
  }
}
